import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { AuthenticatedUser } from '../../../models/identity/authenticated-user.model';
import { AccountsService } from '../../../services/identity/accounts.service';
import { ClubsStore } from '../../clubs.store';
import { ClubActivityService } from '../../services/club-activity.service';
import { Club } from './../../../models/clubs/club.model';

@Component({
    selector: 'club-navigation',
    styleUrls: ['./club-navigation.component.scss'],
    templateUrl: './club-navigation.component.html',
})
export class ClubNavigationComponent implements OnInit, OnDestroy, AfterViewInit {
    public canAccessClubExpore: boolean;
    public canCreateClub: boolean;
    public currentUser: AuthenticatedUser;
    public isCommunityManager: boolean;
    public routerSubscription: Subscription;
    @ViewChild('clubList') clubList: ElementRef<HTMLUListElement>;
    @ViewChild('scrollUpButton') scrollUpButton: ElementRef<HTMLButtonElement>;
    @ViewChild('scrollDownButton') scrollDownButton: ElementRef<HTMLButtonElement>;

    public showScrollUp = false;
    public showScrollDown = false;
    private resizeObserver: ResizeObserver;
    private listChangeObserver: MutationObserver;
    public isLoading = true; 

    constructor(
        readonly clubActivityService: ClubActivityService,
        readonly clubsStore: ClubsStore,
        private readonly accountService: AccountsService,
        private router: Router,
    ) {}

    public get clubsToDisplay(): Club[] {
        return this.clubsStore.member.fullClubs;
    }

    public get isOnClubRoute(): boolean {
        return this.router.url.includes('/clubs/');
    }

    public get selectedClubId(): string {
        return this.clubsStore.displayedClub?.id;
    }

    public getClub(): Club {
        return this.clubsStore.displayedClub;
    }

    public getClubTitle(): string {
        return this.clubsStore.displayedClub?.title;
    }

    public ngOnDestroy() {
        if (this.routerSubscription) {
            this.routerSubscription.unsubscribe();
        }

        // Clean up observers
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.listChangeObserver) {
            this.listChangeObserver.disconnect();
        }
    }

    public async ngOnInit() {
        this.canAccessClubExpore = this.accountService.isAdmin();
        this.canCreateClub = this.accountService.currentUser.primaryCommunity.canCreateClub;
        this.isCommunityManager = this.accountService.isUserManagingCommunity(this.accountService.currentUser.primaryCommunity);

        this.isLoading = true;
        this.clubsStore.loadMember();
        this.clubActivityService.loadAllClubsActivity();
        this.clubsStore.member$.subscribe((member) => {
            if (member) {
                this.isLoading = false;
                setTimeout(() => this.checkScroll(), 100);
            }
        });

        this.routerSubscription = interval(60000).subscribe(() => {
            this.clubActivityService.loadAllClubsActivity();
        });
    }

    public ngAfterViewInit() {
        setTimeout(() => this.initScrollChecks(), 100);
    }

    private initScrollChecks() {
        this.checkScroll();
        this.resizeObserver = new ResizeObserver(() => {
            this.checkScroll();
        });
        if (this.clubList?.nativeElement) {
            this.resizeObserver.observe(this.clubList.nativeElement);
            this.listChangeObserver = new MutationObserver(() => {
                this.checkScroll();
            });
            this.listChangeObserver.observe(this.clubList.nativeElement, {
                childList: true,
                subtree: true,
            });
        }
        window.addEventListener('resize', () => this.checkScroll());
        this.clubsStore.member$.subscribe(() => {
            setTimeout(() => this.checkScroll(), 100);
        });
    }

    public checkScroll() {
        if (!this.clubList?.nativeElement) return;

        const list = this.clubList.nativeElement;
        const scrollTolerance = 1;
        const hasOverflow = list.scrollHeight > list.clientHeight;
        this.showScrollUp = list.scrollTop > scrollTolerance;
        this.showScrollDown = hasOverflow && list.scrollTop + list.clientHeight < list.scrollHeight - scrollTolerance;
    }

    public onScroll() {
        this.checkScroll();
    }

    public scrollList(direction: number) {
        const list = this.clubList?.nativeElement;
        if (list) {
            const scrollAmount = 60;
            list.scrollBy({ top: direction * scrollAmount, behavior: 'smooth' });
            setTimeout(() => this.checkScroll(), 350);
        }
    }
}
